import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Avatar,
  Rating
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Cancel,
  Edit,
  AttachMoney,
  Schedule,
  Description,
  ExpandMore,
  Warning,
  Info,
  TrendingUp,
  TrendingDown,
  CompareArrows,
  Send,
  Verified
} from '@mui/icons-material';
import proposalService from '../../services/proposalService';
import aiService from '../../services/aiService';
import DialogCloseButton from '../common/DialogCloseButton';

const ProposalReviewDialog = ({ open, onClose, proposal, contractor, onProposalAction }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [showChangeRequest, setShowChangeRequest] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [changeRequestData, setChangeRequestData] = useState({
    changes: [],
    message: ''
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    if (open && proposal) {
      loadAIInsights();
    }
  }, [open, proposal]);

  const loadAIInsights = async () => {
    setLoadingInsights(true);
    try {
      // Get AI analysis of the proposal
      const insights = await analyzeProposal(proposal);
      setAiInsights(insights);
    } catch (err) {
      console.error('Failed to load AI insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };

  const analyzeProposal = async (proposal) => {
    // Simplified AI analysis
    const totalPaymentPercentage = proposal.milestones.reduce((sum, m) => sum + (m.paymentPercentage || 0), 0);
    const totalDuration = proposal.milestones.reduce((sum, m) => sum + (m.duration || 0), 0);
    
    return {
      overallScore: 85,
      strengths: [
        'Detailed milestone breakdown',
        'Clear payment schedule',
        'Realistic timeline',
        'Comprehensive scope of work'
      ],
      concerns: [
        totalPaymentPercentage !== 100 ? `Payment percentages total ${totalPaymentPercentage}% (should be 100%)` : null,
        proposal.costBreakdown.contingency < (proposal.totalCost * 0.05) ? 'Contingency budget may be low' : null
      ].filter(Boolean),
      recommendations: [
        'Review milestone deliverables carefully',
        'Clarify any assumptions before accepting',
        'Ensure payment schedule aligns with your cash flow'
      ],
      comparison: {
        budget: 'within_range', // 'below_range', 'within_range', 'above_range'
        timeline: 'reasonable', // 'fast', 'reasonable', 'slow'
        pricing: 'competitive' // 'low', 'competitive', 'high'
      }
    };
  };

  const handleAcceptProposal = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await proposalService.acceptProposal(proposal.id);
      if (result.success) {
        setSuccess('Proposal accepted! Creating contract...');
        setTimeout(() => {
          onProposalAction('accepted', result.data);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.error || 'Failed to accept proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectProposal = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await proposalService.rejectProposal(proposal.id, rejectionReason);
      if (result.success) {
        setSuccess('Proposal rejected');
        setTimeout(() => {
          onProposalAction('rejected', result.data);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.error || 'Failed to reject proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!changeRequestData.message.trim()) {
      setError('Please describe what changes you would like');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await proposalService.requestChanges(proposal.id, changeRequestData);
      if (result.success) {
        setSuccess('Change request sent to contractor');
        setTimeout(() => {
          onProposalAction('changes_requested', result.data);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.error || 'Failed to request changes');
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => (
    <Box>
      <Grid container spacing={3}>
        {/* Contractor Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 60, height: 60 }}>
                  {contractor?.firstName?.[0]}{contractor?.lastName?.[0]}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    {contractor?.firstName} {contractor?.lastName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Rating value={contractor?.rating || 0} readOnly size="small" />
                    <Typography variant="body2" color="text.secondary">
                      ({contractor?.reviewCount || 0} reviews)
                    </Typography>
                    {contractor?.verified && (
                      <Chip icon={<Verified />} label="Verified" size="small" color="primary" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {contractor?.yearsExperience || 0} years experience • {contractor?.completedProjects || 0} projects completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Key Metrics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachMoney color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Cost
                </Typography>
              </Box>
              <Typography variant="h4">
                ${proposal.totalCost?.toLocaleString() || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {proposal.currency || 'USD'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Schedule color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Duration
                </Typography>
              </Box>
              <Typography variant="h4">
                {proposal.milestones?.reduce((sum, m) => sum + (m.duration || 0), 0) || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                weeks
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Description color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Milestones
                </Typography>
              </Box>
              <Typography variant="h4">
                {proposal.milestones?.length || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                phases
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Insights */}
        {aiInsights && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'primary.50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp color="primary" />
                  <Typography variant="h6">
                    AI Analysis
                  </Typography>
                  <Chip label={`Score: ${aiInsights.overallScore}/100`} color="primary" size="small" />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Strengths
                    </Typography>
                    <List dense>
                      {aiInsights.strengths.map((strength, index) => (
                        <ListItem key={index}>
                          <CheckCircle fontSize="small" color="success" sx={{ mr: 1 }} />
                          <ListItemText primary={strength} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  {aiInsights.concerns.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Concerns
                      </Typography>
                      <List dense>
                        {aiInsights.concerns.map((concern, index) => (
                          <ListItem key={index}>
                            <Warning fontSize="small" color="warning" sx={{ mr: 1 }} />
                            <ListItemText primary={concern} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommendations
                    </Typography>
                    <List dense>
                      {aiInsights.recommendations.map((rec, index) => (
                        <ListItem key={index}>
                          <Info fontSize="small" color="info" sx={{ mr: 1 }} />
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Scope of Work */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scope of Work
              </Typography>
              <Typography variant="body2" paragraph>
                {proposal.scopeOfWork}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                {proposal.inclusions?.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Included
                    </Typography>
                    <List dense>
                      {proposal.inclusions.map((item, index) => (
                        <ListItem key={index}>
                          <CheckCircle fontSize="small" color="success" sx={{ mr: 1 }} />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {proposal.exclusions?.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Excluded
                    </Typography>
                    <List dense>
                      {proposal.exclusions.map((item, index) => (
                        <ListItem key={index}>
                          <Cancel fontSize="small" color="error" sx={{ mr: 1 }} />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {proposal.assumptions?.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" gutterBottom>
                      Assumptions
                    </Typography>
                    <List dense>
                      {proposal.assumptions.map((item, index) => (
                        <ListItem key={index}>
                          <Info fontSize="small" color="info" sx={{ mr: 1 }} />
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMilestones = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Project Milestones
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Review the proposed timeline and deliverables for each milestone
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Milestone</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proposal.milestones?.map((milestone, index) => (
              <TableRow key={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {milestone.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {milestone.description}
                  </Typography>
                  {milestone.deliverables && milestone.deliverables.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Deliverables:
                      </Typography>
                      {milestone.deliverables.map((deliverable, idx) => (
                        <Typography key={idx} variant="caption" display="block" sx={{ ml: 1 }}>
                          • {deliverable}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  {milestone.duration} {milestone.durationUnit}
                </TableCell>
                <TableCell>
                  {milestone.paymentPercentage}%
                </TableCell>
                <TableCell>
                  ${((proposal.totalCost * milestone.paymentPercentage) / 100).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3}><strong>Total</strong></TableCell>
              <TableCell>
                <strong>
                  {proposal.milestones?.reduce((sum, m) => sum + (m.paymentPercentage || 0), 0)}%
                </strong>
              </TableCell>
              <TableCell>
                <strong>${proposal.totalCost?.toLocaleString()}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              Start Date
            </Typography>
            <Typography variant="body2">
              {proposal.estimatedStartDate || 'To be determined'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              Completion Date
            </Typography>
            <Typography variant="body2">
              {proposal.estimatedCompletionDate || 'To be determined'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              Working Schedule
            </Typography>
            <Typography variant="body2">
              {proposal.workingSchedule || 'Monday-Friday, 8AM-5PM'}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const renderPricing = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Cost Breakdown
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Category</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Percentage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Materials</TableCell>
              <TableCell align="right">${proposal.costBreakdown?.materials?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {((proposal.costBreakdown?.materials / proposal.totalCost) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Labor</TableCell>
              <TableCell align="right">${proposal.costBreakdown?.labor?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {((proposal.costBreakdown?.labor / proposal.totalCost) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Equipment & Tools</TableCell>
              <TableCell align="right">${proposal.costBreakdown?.equipment?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {((proposal.costBreakdown?.equipment / proposal.totalCost) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Permits & Fees</TableCell>
              <TableCell align="right">${proposal.costBreakdown?.permits?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {((proposal.costBreakdown?.permits / proposal.totalCost) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Contingency</TableCell>
              <TableCell align="right">${proposal.costBreakdown?.contingency?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {((proposal.costBreakdown?.contingency / proposal.totalCost) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Total</strong></TableCell>
              <TableCell align="right"><strong>${proposal.totalCost?.toLocaleString()}</strong></TableCell>
              <TableCell align="right"><strong>100%</strong></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Schedule
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Payments are released upon milestone completion and approval
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Milestone</TableCell>
                <TableCell align="right">Payment</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Due Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposal.paymentSchedule?.map((payment, index) => (
                <TableRow key={index}>
                  <TableCell>{payment.milestoneName}</TableCell>
                  <TableCell align="right">{payment.percentage}%</TableCell>
                  <TableCell align="right">${payment.amount?.toLocaleString()}</TableCell>
                  <TableCell>{payment.dueDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  const renderTerms = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Terms & Conditions
      </Typography>

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
          {proposal.termsAndConditions || 'No terms and conditions specified'}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Warranty
      </Typography>
      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="body2">
          {proposal.warrantyPeriod || 'No warranty specified'}
        </Typography>
      </Paper>
    </Box>
  );

  if (!proposal) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Review Proposal
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Milestones" />
          <Tab label="Pricing" />
          <Tab label="Terms" />
        </Tabs>

        {activeTab === 0 && renderOverview()}
        {activeTab === 1 && renderMilestones()}
        {activeTab === 2 && renderPricing()}
        {activeTab === 3 && renderTerms()}

        {/* Change Request Dialog */}
        {showChangeRequest && (
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'warning.50' }}>
            <Typography variant="h6" gutterBottom>
              Request Changes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Describe the changes you'd like"
              placeholder="Please be specific about what you'd like changed..."
              value={changeRequestData.message}
              onChange={(e) => setChangeRequestData(prev => ({ ...prev, message: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowChangeRequest(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleRequestChanges}
                disabled={loading}
                startIcon={<Send />}
              >
                Send Request
              </Button>
            </Box>
          </Paper>
        )}

        {/* Reject Dialog */}
        {showRejectDialog && (
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'error.50' }}>
            <Typography variant="h6" gutterBottom>
              Reject Proposal
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for rejection"
              placeholder="Please provide a reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleRejectProposal}
                disabled={loading}
              >
                Reject Proposal
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        {!showChangeRequest && !showRejectDialog && (
          <>
            <Button
              color="error"
              onClick={() => setShowRejectDialog(true)}
              disabled={loading}
            >
              Reject
            </Button>
            <Button
              color="warning"
              onClick={() => setShowChangeRequest(true)}
              disabled={loading}
              startIcon={<Edit />}
            >
              Request Changes
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleAcceptProposal}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {loading ? 'Processing...' : 'Accept Proposal'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProposalReviewDialog;
